import type { SxProps, Theme } from "@mui/material";
import { Box, Stack } from "@mui/material";
import type { PropsWithChildren } from "react";
import { Link } from "react-router";
import type { AccessPermission } from "@entrilia/shared/api/types";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import FavoriteStarIcon from "@entrilia/shared/components/FavoriteStarIcon.tsx";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import type { ReportInfo, ReportType } from "../../../../../../api/types/svTypes.ts";
import getReportTypeIcon from "../../../sv/components/common/visualization/icons/getReportTypeIcon.tsx";

interface Props {
  report: Pick<ReportInfo, "name">;
  href: string;
  permissions: AccessPermission[];
  sx?: SxProps<Theme>;
  reportType?: ReportType;
  isFavorite?: boolean;
  isGroupSelected?: boolean;
  onToggleFavorite?: () => void;
}
const NameColumn = ({
  href,
  report,
  permissions,
  sx,
  reportType,
  isFavorite,
  isGroupSelected,
  onToggleFavorite,
  children,
}: PropsWithChildren<Props>): React.JSX.Element => {
  const reportTypeIcon = getReportTypeIcon(reportType);
  return (
    <Stack
      direction={"row"}
      sx={[
        { alignItems: "center", position: "relative", flex: 1, height: "100%", minWidth: 0, overflow: "hidden" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <AuthorizedBox permissions={permissions}>{children}</AuthorizedBox>
      <Stack
        component={Link}
        to={href}
        direction="row"
        className="highlighted-action"
        sx={{
          alignItems: "center",
          flex: 1,
          height: "100%",
          minWidth: 0,
          color: "text.primary",
          textDecoration: "none",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "inline-flex", color: "action.active", pl: isGroupSelected ? 1 : 5.3, flexShrink: 0 }}>
          {reportTypeIcon}
        </Box>
        <TypographyTooltipEllipsis text={report.name} typographyProps={{ sx: { pl: 1 } }} />
      </Stack>
      {onToggleFavorite && (
        <Stack
          className={isFavorite ? undefined : "favorite-star"}
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggleFavorite();
          }}
          sx={{ position: "sticky", right: 0, alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          <FavoriteStarIcon isFavorite={!!isFavorite} />
        </Stack>
      )}
    </Stack>
  );
};

export default NameColumn;
